"use client";

import Image from "next/image";
import {
  forwardRef,
  type ComponentProps,
  type CSSProperties,
  type ImgHTMLAttributes,
} from "react";

function shouldBypassOptimization(src: string) {
  return (
    /^blob:/i.test(src) ||
    /^data:/i.test(src) ||
    src.startsWith("/api/") ||
    src.includes("raw.githubusercontent.com/")
  );
}

type NativeImgProps = Pick<
  ImgHTMLAttributes<HTMLImageElement>,
  | "onLoad"
  | "onClick"
  | "style"
  | "loading"
  | "decoding"
  | "draggable"
  | "referrerPolicy"
>;

type BaseProps = {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  unoptimized?: boolean;
  native?: boolean;
  onError?: ComponentProps<typeof Image>["onError"];
} & NativeImgProps;

type FillProps = BaseProps & {
  fill: true;
  width?: never;
  height?: never;
};

type SizedProps = BaseProps & {
  fill?: false;
  width?: number;
  height?: number;
};

export type AeraImageProps = FillProps | SizedProps;
export type DataTableImageProps = AeraImageProps;

export const AeraImage = forwardRef<HTMLImageElement, AeraImageProps>(
  function AeraImage(props, ref) {
    const {
      src,
      alt,
      className,
      onError,
      onLoad,
      onClick,
      style,
      fill,
      sizes,
      width,
      height,
      priority,
      unoptimized,
      native,
      loading,
      decoding,
      draggable,
      referrerPolicy,
    } = props;

    if (native) {
      return (
        <img
          ref={ref}
          src={src}
          alt={alt}
          className={className}
          onError={onError as ImgHTMLAttributes<HTMLImageElement>["onError"]}
          onLoad={onLoad}
          onClick={onClick}
          style={style}
          loading={loading}
          decoding={decoding}
          draggable={draggable}
          referrerPolicy={referrerPolicy}
        />
      );
    }

    const bypass = unoptimized ?? shouldBypassOptimization(src);

    const shared = {
      ref,
      src,
      alt,
      className,
      unoptimized: bypass,
      priority,
      onError,
      onLoad,
      onClick,
      style: style as CSSProperties | undefined,
      referrerPolicy,
      ...(loading ? { loading } : {}),
    };

    if (fill) {
      return <Image {...shared} fill sizes={sizes ?? "100vw"} />;
    }

    return (
      <Image
        {...shared}
        width={width ?? 20}
        height={height ?? 20}
        sizes={sizes}
      />
    );
  },
);

export const DataTableImage = AeraImage;
